import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, concatMap, map, shareReplay, take, tap } from 'rxjs/operators';

import { ApiCreds } from '@core/models';
import { EmployeeProfileService } from '@core/services';

import * as utils from './utils/service.utils';

import { paystubsCollectionUrl } from './constants';
import { Check, rawPaystubsCollection } from '../models/paystubs-details.interface';

@Injectable({ providedIn: 'root' })
export class PaystubsCollectionService {
  /*** Observable cache of active clean Checks (no void && no future) in descending checkdate order */
  checks$: Observable<Check[]>;

  /*** Observable cache of all clean Checks (void && future) in descending checkdate order */
  allChecks$: Observable<Check[]>;

  constructor(private employeeProfileService: EmployeeProfileService, private http: HttpClient) {
    this.allChecks$ = this.getAllChecks().pipe(
      map(checks => checks.sort(utils.checkDatesDescending)),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.checks$ = this.allChecks$.pipe(
      map(checks => checks.filter(utils.isPastAndNotVoid)),
      shareReplay({ bufferSize: 1, refCount: false })
    );
  }

  /**
   ** Fetch Data from API using Employee API Credentials
   * 1. Build URL string.
   * ___
   *
   * 2. Fetch URL string and create Observable with Data.
   * ___
   */
  private fetchData(apiCreds: ApiCreds): Observable<rawPaystubsCollection> {
    //* 1.
    const url = `${paystubsCollectionUrl}/${apiCreds.bcl}/${apiCreds.id}`;
    //* 2.
    return this.http.get<rawPaystubsCollection>(url).pipe(
      catchError(error => {
        console.error('Getting Checks failed. Returning Empty Array', error);
        return of(null);
      })
    );
  }

  private cleanChecks(rawPaystubsCollectionData: rawPaystubsCollection): Check[] {
    if (rawPaystubsCollectionData) {
      const { Success, ErrorMessage, ...checkData } = rawPaystubsCollectionData;
      const checks = checkData.CheckDetails ?? [];

      return utils.keysToCamelCase(checks);
    } else {
      return [];
    }
  }

  //* Fetch paystub data from API.
  private getAllChecks(): Observable<Check[]> {
    return this.employeeProfileService.apiCreds$.pipe(
      take(1),
      concatMap(apiCreds => this.fetchData(apiCreds)),
      map(rawPaystubsCollection => this.cleanChecks(rawPaystubsCollection))
    );
  }
}
